import { useCallback, useContext, useEffect, useRef, useSyncExternalStore } from "react";

import { CLOCK_CADENCE } from "./clock-cadences";
import { WallClockMountedContext } from "./wall-clock-provider";
import { wallClockService } from "./wall-clock-service";

/**
 * Hook that returns the last tick time (epoch ms) for a given cadence.
 *
 * Re-renders the component when that time changes for the cadence.
 */
export function useWallClock(cadence: CLOCK_CADENCE): number {
    const isMounted = useContext(WallClockMountedContext);
    if (process.env.NODE_ENV !== "production" && !isMounted) {
        throw new Error("useWallClock must be used inside WallClockProvider");
    }
    // Memoized subscribe fns so we dont recreate every render - useSyncExternalStore re-subscribes when `subscribe` changes reference.
    const subscribe = useCallback((listener: () => void) => wallClockService.subscribe(cadence, listener), [cadence]);
    const getSnapshot = useCallback(() => wallClockService.getTickTime(cadence), [cadence]);
    const getServerSnapshot = useCallback(() => 0, []);

    return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * Hook that leverages the useWallClock hook to call a custom callback fn at every "tick" for the given cadence.
 */
export function useWallClockEffect(
    cadence: CLOCK_CADENCE,
    callback: () => void,
    { runOnMount = true }: { runOnMount?: boolean } = {}, // Default to true so it fires once on mount.
): void {
    const lastTickTime = useWallClock(cadence);
    /**
     * Use a ref to keep the latest callback without it being a reactive dep, so callers
     * don't need to memoize and we avoid stale-closure bugs.
     */
    const callbackRef = useRef(callback);
    callbackRef.current = callback;
    const skipNextRef = useRef(!runOnMount);

    /**
     * Run the callback on each tick.
     */
    useEffect(() => {
        if (skipNextRef.current) {
            skipNextRef.current = false;
            return;
        }
        callbackRef.current();
    }, [lastTickTime]);
}
